import { useEffect, useState, JSX, ReactNode } from 'react';
import BaseTour from 'src/components/common/tour/BaseTour';
import TourButton from 'src/components/common/tour/TourButton';
import {
  TourHeader,
  TourSection,
} from 'src/components/common/tour/TourContentTemplates';
import { Icon } from '@iconify/react';
import mathFormula16Filled from '@iconify/icons-fluent/math-formula-16-filled';
import { shouldStartImmediately } from 'src/components/common/tour/tourUtils';
import useAuth from 'src/hooks/useAuth';

interface QSCalculationTourProps {
  children: ReactNode;
  isOpen?: boolean;
  tableIsLoading?: boolean;
}

const QSCalculationTour = ({
  children,
  isOpen = false,
  tableIsLoading = true,
}: QSCalculationTourProps): JSX.Element => {
  const [shouldAutoStart, setShouldAutoStart] = useState(false);
  const { user } = useAuth();

  const isRenter =
    user?.groups &&
    Array.isArray(user.groups) &&
    (user.groups as Array<{ name: string }>).some(
      (group) => group.name.toLowerCase() === 'resident'
    );

  useEffect(() => {
    // Only check for auto-start if tour should be shown and table is not loading
    if (isRenter) {
      return;
    }

    if (tableIsLoading) {
      return;
    }

    // Check if user has dismissed this tour or hasn't seen it yet
    const canAutoStart = shouldStartImmediately('qscCalculationTourData');

    if (canAutoStart) {
      // Add 1-second delay to ensure the DOM is fully rendered and elements are visible
      const timer = setTimeout(() => {
        setShouldAutoStart(true);
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [tableIsLoading, isRenter]);

  const tourSteps = [
    {
      selector: '[data-tour="calculations-tour-button"]',
      content: (
        <TourHeader title="Calculations">
          <TourSection title="Introduction">
            <p>
              Pronto&apos;s calculations
              <Icon
                icon={mathFormula16Filled}
                style={{ marginRight: '8px', color: '#1976d2' }}
              />
              use values entered by you and the renter to automatically
              calculate household income, assets, and more!
              <br />
              <br />
              You&apos;ll see a few types:
            </p>
            <ul style={{ paddingLeft: '20px', marginTop: '12px' }}>
              <li>
                <strong>Household:</strong> Uses values from the whole household
              </li>
              <li>
                <strong>Section:</strong> Uses values entered in that section
              </li>
              <li>
                <strong>Group:</strong> Uses values from grouped questions
              </li>
              <li>
                <strong>Question:</strong> Based on a single answer
              </li>
            </ul>
            <p style={{ marginTop: '12px' }}>
              The formula icon means a value was calculated for you.
            </p>
          </TourSection>
        </TourHeader>
      ),
      position: 'left',
    },
    {
      selector: '[data-tour="filter-controls"]',
      content: (
        <TourHeader title="Calculations">
          <TourSection title="Understanding Calculation Results">
            <p>
              Calculations update automatically as you answer questions or
              review documents.
              <br />
              <br />
              If a calculation is missing required information, you&apos;ll see
              Pending Inputs instead of a number.
              <br />
              <br />
              Use this to spot which answers are still needed to complete the
              file.
            </p>
          </TourSection>
        </TourHeader>
      ),
      position: 'left',
    },
    {
      selector: '[data-tour="household-calculations-tour-card"]',
      content: (
        <TourHeader title="Calculations">
          <TourSection title="Household calculations">
            <p>
              Household calculations appear at the top, sorted by questionnaire
              section. Use these numbers to see a household&apos;s total income
              and assets.
            </p>
          </TourSection>
        </TourHeader>
      ),
      position: 'left',
    },
    {
      selector: '[data-tour="filter-controls"]',
      content: (
        <TourHeader title="Calculations">
          <TourSection title="Filters and Calculation Visibility">
            <p>
              Filtering the summary page to hide answered questions will also
              hide their related calculations.
              <br />
              <br />
              Use filters to focus on the questions and calculations that still
              need review.
            </p>
          </TourSection>
        </TourHeader>
      ),
      position: 'left',
    },
  ];

  // If user is a renter, don't show the tour at all
  if (isRenter) {
    return <>{children}</>;
  }

  return (
    <BaseTour
      steps={tourSteps}
      tourDataKey="qscCalculationTourData"
      initialIsOpen={shouldAutoStart || isOpen}
    >
      {children}
    </BaseTour>
  );
};

// Export the TourButton component that uses the context
const QSCalculationTourButton = (): JSX.Element => (
  <div data-tour="calculations-tour-button">
    <TourButton
      title="Start questionnaire summary calculation tour"
      tourDataKey="qscCalculationTourData"
    />
  </div>
);

export { QSCalculationTour, QSCalculationTourButton as TourButton };
export default QSCalculationTour;
